import { useMutation, useQueryClient } from 'react-query';

import { putV2ApiCompanyIdPurchaseOrdersSettings } from 'src/client/spendesk-api';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { useNotifications } from 'src/core/modules/app/notifications';

import { type PurchaseOrderSettings } from '../../models/purchaseOrderSettings';

type Request = {
  companyId: string;
  settings: PurchaseOrderSettings;
};

export const useSetPurchaseOrderSettings = () => {
  const { t } = useTranslation('global');
  const { dangerNotif, successNotif } = useNotifications();
  const queryClient = useQueryClient();
  const getErrorMessage = (errorKind: string): string => {
    const errorMessages: Record<string, string> = {
      not_found: t(
        'requests.panel.purchaseOrder.deliveryNote.modal.form.errors.notFoundError',
      ),
    };

    return errorMessages[errorKind] || t('errors:somethingWrong');
  };

  return useMutation(
    async ({ companyId, settings }: Request): Promise<void> => {
      try {
        const response = await putV2ApiCompanyIdPurchaseOrdersSettings({
          body: reshapeToFormData({
            companyId,
            settings: {
              commentsAndInstructions: settings.commentsAndInstructions,
              companyLogo: settings.companyLogo,
              defaultEmailSubject: settings.defaultEmailSubject,
              defaultEmailBody: settings.defaultEmailBody,
              autoSendPoEmail: settings.autoSendPoEmail,
            },
          }),
          path: { companyId },
        });

        if (response.statusCode === 200) {
          successNotif(t('purchaseOrders.panel.settings.updateSuccess'));
        } else {
          const errorKind = (response.body.reason as string) ?? 'unknown_error';
          dangerNotif(getErrorMessage(errorKind));
        }
      } catch (error) {
        const errorKind = error.kind ?? 'unknown_error';
        dangerNotif(getErrorMessage(errorKind));
      }
    },
    {
      onSuccess: async (_, params) => {
        await queryClient.invalidateQueries([
          params.companyId,
          'purchase-orders',
          'settings',
        ]);
        await queryClient.invalidateQueries([
          params.companyId,
          'purchase-orders',
          'delivery-notes',
        ]);
      },
    },
  );
};

export const reshapeToFormData = ({
  companyId,
  settings,
}: {
  companyId: string;
  settings: PurchaseOrderSettings;
}): FormData => {
  const formData = new FormData();
  formData.append('companyId', companyId);
  if (
    settings.commentsAndInstructions &&
    settings.commentsAndInstructions !== ''
  ) {
    formData.append(
      'commentsAndInstructions',
      settings.commentsAndInstructions,
    );
  }

  if (settings.companyLogo) {
    formData.append('companyLogo', settings.companyLogo);
  }

  if (settings.defaultEmailSubject && settings.defaultEmailSubject !== '') {
    formData.append('defaultEmailSubject', settings.defaultEmailSubject);
  }

  if (settings.defaultEmailBody && settings.defaultEmailBody !== '') {
    formData.append('defaultEmailBody', settings.defaultEmailBody);
  }

  if (typeof settings.autoSendPoEmail === 'boolean') {
    formData.append('autoSendPoEmail', String(settings.autoSendPoEmail));
  }

  return formData;
};
